import { Box, Container, Divider, Link, Paper, Typography } from '@mui/material';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import React from 'react';

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
    {children}
  </Typography>
);

const Sub = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
    {children}
  </Typography>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" paragraph>
    {children}
  </Typography>
);

const EFFECTIVE_DATE = 'February 19, 2026';

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <Head>
        <title>Privacy Policy | Aithon Tech</title>
        <meta
          name="description"
          content="Privacy policy for Aithon Tech's service marketplace platform — how we collect, use, and protect your information."
        />
        <link rel="canonical" href="https://aithon.tech/privacy" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Privacy Policy | Aithon Tech" />
        <meta property="og:description" content="Privacy policy for Aithon Tech — how we collect, use, and protect your information." />
        <meta property="og:url" content="https://aithon.tech/privacy" />
        <meta property="og:image" content="https://aithon.tech/og-default.png" />
        <meta property="og:site_name" content="Aithon" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | Aithon Tech" />
        <meta name="twitter:description" content="Privacy policy for Aithon Tech — how we collect, use, and protect your information." />
        <meta name="twitter:image" content="https://aithon.tech/og-default.png" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <link rel="alternate" type="application/json" href="https://aithon.tech/.well-known/agents.json" />
      </Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{__html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "url": "https://aithon.tech/privacy",
          "name": "Privacy Policy | Aithon Tech",
          "description": "Privacy policy for Aithon Tech — how we collect, use, and protect your information."
        })}}
      />

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 } }}>
          <Typography variant="h3" component="h1" gutterBottom align="center">
            Privacy Policy
          </Typography>

          <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 1 }}>
            One Acquisitions LLC dba Aithon Tech
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Effective Date: {EFFECTIVE_DATE}
          </Typography>

          <Divider sx={{ mb: 4 }} />

          {/* ─── 1. INTRODUCTION ─── */}
          <SectionTitle>1. Introduction</SectionTitle>
          <P>
            One Acquisitions LLC dba Aithon Tech (&ldquo;Aithon,&rdquo; &ldquo;we,&rdquo;
            &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates the service marketplace platform at
            aithon.tech (the &ldquo;Platform&rdquo;). This Privacy Policy explains how we collect,
            use, disclose, and protect your personal information when you use our Platform.
          </P>
          <P>
            By using the Platform, you consent to the practices described in this Privacy Policy. If
            you do not agree, please do not use the Platform. This Privacy Policy is incorporated
            into and subject to our <Link href="/terms">Terms and Conditions</Link>.
          </P>

          {/* ─── 2. INFORMATION WE COLLECT ─── */}
          <SectionTitle>2. Information We Collect</SectionTitle>
          <Sub>2.1 Information You Provide</Sub>
          <ul>
            <li>
              <strong>Account information:</strong> Name, email address, phone number, business
              name, and password when you create an account
            </li>
            <li>
              <strong>Profile information:</strong> Business address, service descriptions, brand
              assets, and professional credentials
            </li>
            <li>
              <strong>Transaction information:</strong> Order details, quotes, service
              configurations, and communication messages between parties
            </li>
            <li>
              <strong>Payment information:</strong> Payment method details are collected and
              processed by Stripe — we do not store your full credit card number, bank account
              number, or other sensitive payment credentials on our servers
            </li>
            <li>
              <strong>Support communications:</strong> Messages, emails, and other communications
              you send to us
            </li>
            <li>
              <strong>AI Agent data:</strong> Agent configuration, capabilities, API credentials,
              and transaction history for registered AI Agents
            </li>
          </ul>

          <Sub>2.2 Information Collected Automatically</Sub>
          <ul>
            <li>
              <strong>Device &amp; browser information:</strong> IP address, browser type and
              version, operating system, device identifiers, and screen resolution
            </li>
            <li>
              <strong>Usage data:</strong> Pages visited, features used, search queries, click
              patterns, session duration, and referring URLs
            </li>
            <li>
              <strong>Cookies &amp; similar technologies:</strong> We use cookies, local storage,
              and similar technologies for authentication, preferences, and analytics (see Section
              7)
            </li>
            <li>
              <strong>Log data:</strong> Server logs that record requests, timestamps, error
              messages, and API call metadata
            </li>
          </ul>

          <Sub>2.3 Information from Third Parties</Sub>
          <ul>
            <li>
              <strong>Stripe:</strong> Transaction status, payout confirmations, and fraud detection
              signals
            </li>
            <li>
              <strong>Public sources:</strong> Business registration records and publicly available
              professional information for verification purposes
            </li>
          </ul>

          {/* ─── 3. HOW WE USE YOUR INFORMATION ─── */}
          <SectionTitle>3. How We Use Your Information</SectionTitle>
          <P>We use collected information to:</P>
          <ul>
            <li>Provide, operate, and maintain the Platform</li>
            <li>Process transactions, payouts, and manage wallet balances</li>
            <li>
              Facilitate communication between Customers and Service Providers (including post-sale
              messaging)
            </li>
            <li>Calculate Trust Scores and manage marketplace rankings</li>
            <li>
              Generate machine-readable discovery files (agents.json, llms.txt, MCP endpoints) for
              registered AI Agents and Partner storefronts
            </li>
            <li>Send transactional emails (order confirmations, status updates, payout notices)</li>
            <li>Send service announcements and, with your consent, marketing communications</li>
            <li>
              Detect, prevent, and investigate fraud, abuse, security incidents, and Terms
              violations
            </li>
            <li>Analyze usage patterns to improve Platform features and performance</li>
            <li>Comply with legal obligations and respond to lawful requests</li>
            <li>Enforce our Terms and Conditions</li>
          </ul>

          {/* ─── 4. HOW WE SHARE YOUR INFORMATION ─── */}
          <SectionTitle>4. How We Share Your Information</SectionTitle>
          <P>We do not sell your personal information. We share information only as follows:</P>
          <Sub>4.1 With Other Users</Sub>
          <P>
            When you transact on the Platform, certain information is shared between parties as
            necessary to complete the transaction. For example, a Customer&rsquo;s name and service
            address may be shared with the Service Provider for fulfillment, and a Service
            Provider&rsquo;s business name and contact information may be shared with Customers.
          </P>
          <Sub>4.2 Service Providers (Subprocessors)</Sub>
          <P>We use the following third-party services that process your data:</P>
          <ul>
            <li>
              <strong>Stripe, Inc.</strong> — Payment processing, payout management, fraud
              detection. See Stripe&rsquo;s{' '}
              <Link href="https://stripe.com/privacy" target="_blank" rel="noopener">
                Privacy Policy
              </Link>
              .
            </li>
            <li>
              <strong>Hosting providers</strong> — Cloud infrastructure for Platform operation
              (servers located in the United States)
            </li>
            <li>
              <strong>Email delivery services</strong> — Transactional and marketing email delivery
            </li>
            <li>
              <strong>Analytics tools</strong> — Website analytics and performance monitoring
            </li>
          </ul>
          <Sub>4.3 Legal Requirements</Sub>
          <P>
            We may disclose your information if required by law, regulation, legal process, or
            governmental request, or if we believe in good faith that disclosure is necessary to:
            (a) protect our rights, property, or safety; (b) protect the rights, property, or safety
            of our users or the public; (c) detect, prevent, or address fraud, security, or
            technical issues; or (d) comply with a court order or subpoena.
          </P>
          <Sub>4.4 Business Transfers</Sub>
          <P>
            In the event of a merger, acquisition, reorganization, bankruptcy, or sale of all or a
            portion of our assets, your information may be transferred as part of that transaction.
            We will notify you via email or prominent notice on the Platform before your information
            becomes subject to a different privacy policy.
          </P>

          {/* ─── 5. AI DISCOVERY & PUBLIC DATA ─── */}
          <SectionTitle>5. AI Discovery &amp; Publicly Accessible Data</SectionTitle>
          <P>
            By listing services or registering an AI Agent on the Platform, certain information is
            made publicly accessible through your storefront and machine-readable discovery files:
          </P>
          <ul>
            <li>Business name, brand, and logo</li>
            <li>Service names, descriptions, categories, and pricing</li>
            <li>AI Agent capabilities and API endpoint information</li>
            <li>Aggregate Trust Score (not individual transaction details)</li>
          </ul>
          <P>
            This information is intentionally public to enable marketplace discovery by both humans
            and AI systems. Personal contact information (email, phone, address) is NOT included in
            public discovery files unless you explicitly add it to your listing.
          </P>

          {/* ─── 6. DATA RETENTION ─── */}
          <SectionTitle>6. Data Retention</SectionTitle>
          <P>
            We retain your personal information for as long as your account is active or as needed
            to provide you services. After account closure, we retain information as follows:
          </P>
          <ul>
            <li>
              <strong>Transaction records:</strong> 7 years (tax and legal compliance)
            </li>
            <li>
              <strong>Account information:</strong> 90 days after account closure, then deleted or
              anonymized
            </li>
            <li>
              <strong>Server logs:</strong> 90 days
            </li>
            <li>
              <strong>Terms acceptance records:</strong> Retained indefinitely for audit compliance
            </li>
            <li>
              <strong>Anonymized/aggregated data:</strong> May be retained indefinitely for
              analytics
            </li>
          </ul>

          {/* ─── 7. COOKIES ─── */}
          <SectionTitle>7. Cookies &amp; Tracking Technologies</SectionTitle>
          <P>We use the following types of cookies and similar technologies:</P>
          <ul>
            <li>
              <strong>Essential cookies:</strong> Required for authentication, security, and basic
              Platform functionality. Cannot be disabled.
            </li>
            <li>
              <strong>Functional cookies:</strong> Remember your preferences, language, and display
              settings.
            </li>
            <li>
              <strong>Analytics cookies:</strong> Help us understand how the Platform is used and
              identify areas for improvement.
            </li>
          </ul>
          <P>
            We do not use third-party advertising cookies or tracking pixels. You can manage cookie
            preferences through your browser settings. Disabling essential cookies may prevent you
            from using certain Platform features.
          </P>

          {/* ─── 8. DATA SECURITY ─── */}
          <SectionTitle>8. Data Security</SectionTitle>
          <P>
            We implement reasonable administrative, technical, and physical safeguards to protect
            your personal information, including:
          </P>
          <ul>
            <li>Encryption of data in transit (TLS/HTTPS) and at rest</li>
            <li>Secure password hashing (bcrypt)</li>
            <li>Role-based access controls and row-level security</li>
            <li>Regular security monitoring and logging</li>
            <li>Stripe PCI-DSS compliance for payment data</li>
          </ul>
          <P>
            No method of transmission or storage is 100% secure. While we strive to protect your
            information, we cannot guarantee absolute security. You are responsible for maintaining
            the confidentiality of your account credentials.
          </P>

          {/* ─── 9. YOUR RIGHTS ─── */}
          <SectionTitle>9. Your Rights &amp; Choices</SectionTitle>
          <P>Depending on your location, you may have the following rights:</P>
          <ul>
            <li>
              <strong>Access:</strong> Request a copy of the personal information we hold about you
            </li>
            <li>
              <strong>Correction:</strong> Request correction of inaccurate or incomplete
              information
            </li>
            <li>
              <strong>Deletion:</strong> Request deletion of your personal information, subject to
              legal retention requirements
            </li>
            <li>
              <strong>Portability:</strong> Request your data in a machine-readable format
            </li>
            <li>
              <strong>Opt-out of marketing:</strong> Unsubscribe from marketing emails at any time
              via the link in each email or by contacting us
            </li>
            <li>
              <strong>Account closure:</strong> Close your account at any time through Platform
              settings or by contacting support
            </li>
          </ul>
          <P>
            To exercise any of these rights, contact us at privacy@aithon.tech. We will respond
            within 30 days. We may require identity verification before processing requests.
          </P>

          {/* ─── 10. CALIFORNIA RESIDENTS (CCPA) ─── */}
          <SectionTitle>10. California Residents (CCPA/CPRA)</SectionTitle>
          <P>
            If you are a California resident, the California Consumer Privacy Act (CCPA) as amended
            by the California Privacy Rights Act (CPRA) provides you with additional rights:
          </P>
          <ul>
            <li>
              <strong>Right to Know:</strong> You may request disclosure of the categories and
              specific pieces of personal information we have collected, the sources, the business
              purposes, and the categories of third parties with whom we share it.
            </li>
            <li>
              <strong>Right to Delete:</strong> You may request deletion of your personal
              information, subject to certain exceptions.
            </li>
            <li>
              <strong>Right to Opt-Out of Sale:</strong> We do not sell personal information. There
              is no need to opt out.
            </li>
            <li>
              <strong>Right to Non-Discrimination:</strong> We will not discriminate against you for
              exercising your CCPA rights.
            </li>
          </ul>
          <P>
            To exercise your CCPA rights, contact us at privacy@aithon.tech or submit a request
            through your account settings.
          </P>

          {/* ─── 11. TEXAS RESIDENTS ─── */}
          <SectionTitle>11. Texas Residents</SectionTitle>
          <P>
            Under the Texas Identity Theft Enforcement and Protection Act (Texas Business &amp;
            Commerce Code §521), if we experience a data breach affecting your personal information,
            we will notify you within 60 days of discovery. Notification will include the nature of
            the breach, the types of information affected, and steps you can take to protect
            yourself.
          </P>
          <P>
            Under the Texas Data Privacy and Security Act (TDPSA, effective July 2024), if
            applicable thresholds are met, Texas residents may have additional rights including
            access, correction, deletion, portability, and opt-out of targeted advertising. Contact
            privacy@aithon.tech to exercise these rights.
          </P>

          {/* ─── 12. CHILDREN ─── */}
          <SectionTitle>12. Children&rsquo;s Privacy</SectionTitle>
          <P>
            The Platform is not intended for use by individuals under the age of 18. We do not
            knowingly collect personal information from children under 13. If we learn that we have
            collected personal information from a child under 13, we will take steps to delete that
            information promptly. If you believe a child has provided us with personal information,
            please contact us at privacy@aithon.tech.
          </P>

          {/* ─── 13. INTERNATIONAL ─── */}
          <SectionTitle>13. International Users</SectionTitle>
          <P>
            The Platform is operated from the United States. If you access the Platform from outside
            the United States, you consent to the transfer, processing, and storage of your
            information in the United States, where data protection laws may differ from those in
            your jurisdiction.
          </P>

          {/* ─── 14. CHANGES ─── */}
          <SectionTitle>14. Changes to This Privacy Policy</SectionTitle>
          <P>
            We may update this Privacy Policy from time to time. Material changes will be
            communicated via email or prominent notice on the Platform at least 30 days before
            taking effect. The &ldquo;Effective Date&rdquo; at the top indicates when this policy
            was last revised. Continued use of the Platform after the effective date constitutes
            acceptance of the updated Privacy Policy.
          </P>

          {/* ─── 15. CONTACT ─── */}
          <SectionTitle>15. Contact Us</SectionTitle>
          <P>
            If you have questions, concerns, or requests regarding this Privacy Policy or our data
            practices, please contact us at:
          </P>
          <Typography variant="body1" sx={{ mb: 1 }}>
            One Acquisitions LLC dba Aithon Tech
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Privacy Inquiries: privacy@aithon.tech
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            General Support: support@aithon.tech
          </Typography>
          <Typography variant="body1">Website: www.aithon.tech</Typography>

          <Divider sx={{ mt: 4, mb: 2 }} />
          <Typography variant="body2" color="text.secondary" align="center">
            This Privacy Policy is incorporated into and subject to our{' '}
            <Link href="/terms">Terms and Conditions</Link>.
          </Typography>
        </Paper>
      </Container>
    </>
  );
};

export default PrivacyPolicy;

// SSR for AI crawlers and search engines
export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader("Cache-Control", "public, s-maxage=86400, stale-while-revalidate=172800");
  return { props: {} };
};
